import type { OfflineDatabaseSqlStatement } from './offlineDatabase.statements';

export type OfflineDatabaseStatsKey =
  | 'reviews'
  | 'responses'
  | 'photosQueue'
  | 'cachedTemplates'
  | 'cachedCategories'
  | 'cachedItems'
  | 'cachedRooms';

export type OfflineDatabaseStatsStatements = Record<
  OfflineDatabaseStatsKey,
  OfflineDatabaseSqlStatement
>;

const OFFLINE_DATABASE_ROW_COUNT_TABLES = [
  'reviews',
  'responses',
  'photos_queue',
  'photos_metadata',
  'templates_cache',
  'review_template_snapshots',
  'categories_cache',
  'items_cache',
  'rooms_cache',
  'db_metadata',
  'cache_metadata',
  'offline_on_demand_items',
  'offline_rooms',
  'id_mappings',
  'shared_category_links',
  'shared_item_links',
  'shared_question_mappings',
  'review_response_provenance',
] as const;

export type OfflineDatabaseRowCountTable =
  typeof OFFLINE_DATABASE_ROW_COUNT_TABLES[number];

export const isOfflineDatabaseRowCountTable = (
  tableName: string
): tableName is OfflineDatabaseRowCountTable => (
  (OFFLINE_DATABASE_ROW_COUNT_TABLES as readonly string[]).includes(tableName)
);

export const buildRowCountStatement = (
  tableName: OfflineDatabaseRowCountTable
): OfflineDatabaseSqlStatement => ({
  sql: `SELECT COUNT(*) as count FROM ${tableName}`,
  params: [],
});

export const buildExpiredCacheDeleteStatements = (
  now: string
): OfflineDatabaseSqlStatement[] => [
  {
    sql: 'DELETE FROM categories_cache WHERE expires_at <= ?',
    params: [now],
  },
  {
    sql: 'DELETE FROM items_cache WHERE expires_at <= ?',
    params: [now],
  },
  {
    sql: 'DELETE FROM rooms_cache WHERE expires_at <= ?',
    params: [now],
  },
];

export const buildStaleTemplatesCacheDeleteStatement = (
  threshold: string
): OfflineDatabaseSqlStatement => ({
  sql: 'DELETE FROM templates_cache WHERE expires_at <= ?',
  params: [threshold],
});

export const buildDatabaseStatsStatements = (
  now: string,
  environmentKey?: string
): OfflineDatabaseStatsStatements => ({
  reviews: {
    sql: `SELECT COUNT(*) as count FROM reviews${environmentKey ? ' WHERE environment_key = ?' : ''}`,
    params: environmentKey ? [environmentKey] : [],
  },
  responses: {
    sql: `SELECT COUNT(*) as count FROM responses${environmentKey ? ' WHERE environment_key = ?' : ''}`,
    params: environmentKey ? [environmentKey] : [],
  },
  photosQueue: {
    sql: `SELECT COUNT(*) as count FROM photos_queue${environmentKey ? ' WHERE environment_key = ?' : ''}`,
    params: environmentKey ? [environmentKey] : [],
  },
  cachedTemplates: {
    sql: 'SELECT COUNT(*) as count FROM templates_cache WHERE expires_at > ?',
    params: [now],
  },
  cachedCategories: {
    sql: 'SELECT COUNT(*) as count FROM categories_cache WHERE expires_at > ?',
    params: [now],
  },
  cachedItems: {
    sql: 'SELECT COUNT(*) as count FROM items_cache WHERE expires_at > ?',
    params: [now],
  },
  cachedRooms: {
    sql: 'SELECT COUNT(*) as count FROM rooms_cache WHERE expires_at > ?',
    params: [now],
  },
});
